import { Button, Exception } from '@yimoka/antd';
import type { ReactNode } from 'react';
import { Component } from 'react';

import { t } from '@/locales/i18n';

interface ErrorBoundaryProps {
  children: ReactNode;
  onRetry?: () => void;
}

interface ErrorBoundaryState {
  error?: Error;
}

/** ErrorBoundary 将运行时异常呈现为旧版图文错误页，并允许调用方重新加载业务数据。 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  /** getDerivedStateFromError 将子树异常转为可恢复的渲染状态，避免整页白屏。 */
  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  state: ErrorBoundaryState = {};

  /** render 在正常时透明渲染子树，异常时提供带原因和重试入口的可见反馈。 */
  render() {
    const { children, onRetry } = this.props;
    const { error } = this.state;
    if (!error) return children;

    return (
      <Exception
        actions={(
          <Button
            type="primary"
            onClick={() => {
              // 先清除 boundary 状态，子树才能重新挂载；只重试请求而不清状态会持续被旧错误遮挡。
              this.setState({ error: undefined });
              onRetry?.();
            }}
          >
            {t('Common:retry')}
          </Button>
        )}
        desc={(
          <>
            <div>{t('Common:pageErrorDescription')}</div>
            <div>{error.message}</div>
          </>
        )}
        title={t('Common:pageErrorTitle')}
        type="500"
      />
    );
  }
}
